import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SITE } from "../data/site";

const STEPS = [
  { t: "Survey", d: "actual system visibility" },
  { t: "Plan", d: "agreed scope and windows" },
  { t: "Execute", d: "specialist cleaning" },
  { t: "Verify", d: "evidence against the survey" },
  { t: "Handover", d: "documentation you can file" },
];

// SCENE 05: "Job done." collapses into the DCC standard.
export function WhyDcc() {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const doneO = useTransform(p, [0, 0.08, 0.3, 0.42], [0, 1, 1, 0]);
  const strike = useTransform(p, [0.16, 0.3], ["0%", "100%"]);
  const doneY = useTransform(p, [0.3, 0.42], [0, -50]);
  const stepsO = useTransform(p, [0.4, 0.5], [0, 1]);
  const lineScale = useTransform(p, [0.45, 0.9], [0, 1]);
  const tailO = useTransform(p, [0.86, 0.95], [0, 1]);

  // Static version — reduced-motion + mobile (no pinned scroll on touch).
  const staticSteps = (
    <section className="bg-paper py-16">
      <div className="container-x">
        <p className="font-display text-4xl uppercase text-zinc-500 line-through">“Job done.”</p>
        <h2 className="h-display mt-8 text-4xl text-charcoal sm:text-5xl">One accountable process.</h2>
        <ol className="mt-10 space-y-4">
          {STEPS.map((s, i) => (
            <li key={s.t} className="flex items-baseline gap-4 border-b border-zinc-200 pb-4">
              <span className="tech-no">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-display text-2xl uppercase text-charcoal">{s.t}</span>
              <span className="text-sm text-zinc-500">— {s.d}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );

  // Both variants render — CSS decides visibility (no hydration layout swap).
  return (
    <>
      <div className="md:hidden md:motion-reduce:block">{staticSteps}</div>
      <div className="hidden md:block md:motion-reduce:hidden">
        <section ref={ref} className="relative h-[145vh]" aria-label="Why DCC">
      <div className="sticky top-0 h-screen overflow-hidden bg-paper">
        <div className="absolute inset-0 grid-lines" aria-hidden="true" />
        <div className="container-x relative flex h-full flex-col justify-center">
          <p className="absolute top-24 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 md:top-28">
            03 / Why facility teams choose DCC
          </p>

          {/* commodity phrase */}
          <motion.div style={{ opacity: doneO, y: doneY }} className="absolute">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">A commodity contractor says:</p>
            <div className="relative mt-6 inline-block">
              <p className="h-display text-[16vw] leading-none text-zinc-300 sm:text-[12vw] lg:text-[9vw]">“JOB DONE.”</p>
              <motion.span style={{ width: strike }} className="absolute left-0 top-1/2 h-[0.06em] bg-dcc" aria-hidden="true" />
            </div>
            <p className="mt-4 max-w-sm text-sm text-zinc-500">For a concealed system, that sentence is unfalsifiable. You cannot see inside the duct — so “done” means whatever they say it means.</p>
          </motion.div>

          {/* the DCC standard */}
          <motion.div style={{ opacity: stepsO }} className="absolute inset-x-6 md:inset-x-12 lg:inset-x-20">
            <div className="flex gap-8 md:gap-14">
              <motion.div style={{ scaleY: lineScale }} className="w-px shrink-0 origin-top self-stretch bg-dcc" aria-hidden="true" />
              <ol className="flex-1">
                {STEPS.map((s, i) => (
                  <StepRow key={s.t} s={s} i={i} p={p} />
                ))}
              </ol>
            </div>
            <motion.p style={{ opacity: tailO }} className="mt-10 max-w-xl text-sm leading-relaxed text-zinc-600 md:text-base">
              One accountable process — from first inspection to final handover. <span className="font-mono text-xs uppercase tracking-[0.2em] text-dcc-strong">{SITE.methodology.join(" → ")}</span>
            </motion.p>
          </motion.div>
        </div>
      </div>
        </section>
      </div>
    </>
  );
}

function StepRow({ s, i, p }) {
  const start = 0.45 + i * 0.09;
  const opacity = useTransform(p, [start, start + 0.06], [0, 1]);
  const x = useTransform(p, [start, start + 0.06], [50, 0]);
  return (
    <motion.li style={{ opacity, x }} className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-b border-zinc-200 py-4 md:py-5">
      <span className="tech-no">{String(i + 1).padStart(2, "0")}</span>
      <span className="h-display text-3xl text-charcoal sm:text-5xl lg:text-6xl">{s.t.toUpperCase()}</span>
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">→ {s.d}</span>
    </motion.li>
  );
}
